import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Tooltip, Alert, Space } from 'antd';
import { FunctionOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const MetricConfigModal = ({
  visible,
  onCancel,
  onSave,
  editingMetric,
  presetDimensions,
  customDimensions
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (editingMetric) {
        // 编辑模式，填充表单
        form.setFieldsValue({
          name: editingMetric.name,
          code: editingMetric.code,
          description: editingMetric.description || '',
          formula: editingMetric.formula || ''
        });
      } else {
        // 新增模式，重置表单
        form.resetFields();
      }
    }
  }, [visible, editingMetric, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSave(values);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const validateCode = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('请输入指标代码'));
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      return Promise.reject(new Error('指标代码只能包含字母、数字和下划线'));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      title={
        <Space>
          <FunctionOutlined />
          {editingMetric ? '编辑自定义指标' : '添加自定义指标'}
        </Space>
      }
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={600}
      okText="确定"
      cancelText="取消"
      destroyOnClose
    >
      <Alert
        message="提示"
        description="自定义指标用于定义业务特定的评测标准。该指标将添加到当前维度中，与该维度的5个预设指标一起进行统计计算。"
        type="info"
        showIcon
        className="mb-4"
      />

      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          label="指标名称"
          name="name"
          rules={[
            { required: true, message: '请输入指标名称' },
            { max: 30, message: '指标名称不能超过30个字符' }
          ]}
        >
          <Input
            placeholder="例如：综合评分、业务准确率"
            maxLength={30}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              指标代码
              <Tooltip title="用于在脚本中引用该指标，建议使用字母、数字和下划线的组合">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </Space>
          }
          name="code"
          rules={[{ validator: validateCode }]}
        >
          <Input
            placeholder="例如：custom_score、CUSTOM_SCORE"
            maxLength={30}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="指标描述"
          name="description"
          rules={[
            { required: true, message: '请输入指标描述' },
            { max: 200, message: '描述不能超过200个字符' }
          ]}
        >
          <TextArea 
            placeholder="详细描述该指标的计算方式和业务含义"
            rows={3}
            maxLength={200}
            showCount
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              计算公式
              <Tooltip title="可选字段，用于记录指标的计算逻辑">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </Space>
          }
          name="formula"
        >
          <TextArea 
            placeholder="例如：(precision + recall) / 2"
            rows={2}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>

      <Alert
        message="指标配置说明"
        description={
          <div className="space-y-2 text-sm">
            <div><strong>指标位置</strong>：该自定义指标将添加到当前维度中，与5个预设指标（无风险精确率、无风险召回率、有风险精确率、有风险召回率、准确率）一起使用</div>
            <div><strong>指标代码</strong>：用于在脚本中引用该指标，可使用字母、数字和下划线的组合</div>
            <div><strong>计算公式</strong>：可选字段，用于记录指标的计算逻辑，便于后续维护和理解</div>
          </div>
        }
        type="info"
        showIcon
        className="mt-4"
      />
    </Modal>
  );
};

export default MetricConfigModal;
